import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { DEFAULT_ITEMS_PER_VIEW_DESKTOP } from '@components/gallery/components/gallery-layout-16/config';
import { GalleryLayout16 } from '@components/gallery/components/gallery-layout-16/model';
import { ResponsiveService } from '@services/responsive';
import { ButtonComponent } from '@shared/components/button';
import { ImageComponent } from '@shared/components/image';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { IconsDirective } from '@shared/directives';
import { PageStore } from '../../../../stores';

@Component({
  selector: 'app-gallery-layout-16',
  imports: [TitleComponent, IconsDirective, ButtonComponent, NgClass, ImageComponent, RouterLink],
  templateUrl: './gallery-layout-16.component.html',
  styleUrl: './gallery-layout-16.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryLayout16Component implements OnInit {
  readonly listElement = viewChild.required<ElementRef<HTMLElement>>('listElement');
  readonly data = input.required<GalleryLayout16>();
  title = signal<Title>(null!);
  subtitle = signal<Title>(null!);
  currentScroll = signal(0);
  readonly breakpoints = inject(ResponsiveService).breakpoints;
  protected readonly uuid = inject(PageStore).selectPath((state) => state.config.uuid);
  protected readonly DEFAULT_ITEMS_PER_VIEW_DESKTOP = DEFAULT_ITEMS_PER_VIEW_DESKTOP;
  readonly backgroundClass = computed(() => {
    const theme = this.data().theme;
    if (theme) return theme.toLowerCase();
    return this.data().isBackgroundWhite ? 'white' : 'blue';
  });
  readonly isLightBackground = computed(() => {
    const theme = this.data().theme;
    if (theme) return theme === 'WHITE' || theme === 'OPAL';
    return !!this.data().isBackgroundWhite;
  });

  ngOnInit(): void {
    const data = this.data();
    const isLightBackground = this.isLightBackground();
    if (data.title) {
      this.title.set({
        size: {
          desktop: 'H2',
          mobile: 'H2',
        },
        text: data.title,
        level: 'SPAN',
        color: isLightBackground ? 'BLACK' : 'WHITE',
      });
    }
    if (data.subtitle) {
      this.subtitle.set({
        size: {
          desktop: 'SPAN',
          mobile: 'SPAN',
        },
        type: 'SLASH',
        weight: 500,
        text: data.subtitle,
        level: 'H2',
        color: isLightBackground ? 'GREY_LIGHT' : 'WHITE',
      });
    }
  }

  onNext(length: number) {
    const stride = this.#pageStride();
    if (this.currentScroll() < stride * (length - 1)) {
      this.currentScroll.set(this.currentScroll() + stride);
    } else {
      this.currentScroll.set(0);
    }

    this.listElement().nativeElement.scrollTo({ left: this.currentScroll() });
  }

  onPrev(length: number) {
    const stride = this.#pageStride();
    if (this.currentScroll() > 0) {
      this.currentScroll.set(this.currentScroll() - stride);
    } else {
      this.currentScroll.set(stride * (length - 1));
    }
    this.listElement().nativeElement.scrollTo({ left: this.currentScroll() });
  }

  #pageStride(): number {
    const list = this.listElement().nativeElement;
    const lastPlusOne = list.children[DEFAULT_ITEMS_PER_VIEW_DESKTOP] as HTMLElement | undefined;
    return lastPlusOne?.offsetLeft ?? list.clientWidth;
  }
}
